import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import DatePicker from 'react-datepicker';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useInvoice } from '../context/InvoiceContext';
import CustomerSelector from './CustomerSelector';
import 'react-datepicker/dist/react-datepicker.css';

const { FiPlus, FiTrash2, FiSave, FiHash, FiMapPin, FiCalendar, FiUsers, FiClock, FiUpload, FiX, FiFileText, FiArrowLeft, FiCheckSquare, FiCheckCircle } = FiIcons;

const ExcursionBookingEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { invoices, updateInvoice, settings } = useInvoice();
  const existingBooking = invoices.find(inv => inv.id === parseInt(id));

  const [formData, setFormData] = useState(null);

  const currencies = [
    { code: 'EUR', symbol: '€', name: 'Euro' },
    { code: 'USD', symbol: '$', name: 'US Dollar' },
    { code: 'GBP', symbol: '£', name: 'British Pound' }
  ];

  const languages = ['English', 'French', 'Spanish', 'German', 'Italian', 'Portuguese'];
  const difficulties = ['Easy', 'Moderate', 'Challenging', 'Difficult'];

  useEffect(() => {
    if (existingBooking) {
      setFormData({
        ...existingBooking,
        customer: existingBooking.customer || { name: '', address: '', phone: '', email: '' },
        bookingNumber: existingBooking.number || existingBooking.bookingNumber || 1,
        excursionDetails: {
          title: '',
          destination: '',
          tourOperator: '',
          tourOperatorContact: '',
          duration: '',
          meetingPoint: '',
          meetingTime: '',
          dropOffLocation: '',
          participants: 1,
          language: 'English',
          difficulty: 'Easy',
          includes: [''],
          excludes: [''],
          specialRequirements: '',
          ...existingBooking.excursionDetails,
          excursionDate: existingBooking.excursionDetails?.excursionDate ? new Date(existingBooking.excursionDetails.excursionDate) : new Date(),
        },
        pricing: {
          priceType: 'per_person',
          adultPrice: 0,
          childPrice: 0,
          adultCount: 1,
          childCount: 0,
          groupPrice: 0,
          conciergeService: 0,
          currency: 'EUR',
          ...existingBooking.pricing,
        },
        paymentStatus: {
          conciergeServicePaid: false,
          organizationFeePaid: false,
          ...existingBooking.paymentStatus,
        },
        operatorInfo: {
          confirmationEmail: '',
          contractDetails: '',
          cancellationPolicy: '',
          emergencyContact: '',
          additionalNotes: '',
          ...existingBooking.operatorInfo,
        },
        attachments: existingBooking.attachments || [],
        status: existingBooking.status || 'paid', // Default to paid for excursions
        invoiceType: 'excursion'
      });
    }
  }, [existingBooking]);

  if (!formData) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <h3 className="text-lg font-medium text-gray-900 mb-2">Loading...</h3>
            <p className="text-gray-600">If the booking doesn't load, it may not exist.</p>
          </div>
        </div>
      </div>
    );
  }

  const getCurrencySymbol = (code) => currencies.find(c => c.code === code)?.symbol || '€';

  const handleCustomerChange = (field, value) => {
    setFormData(prev => ({ ...prev, customer: { ...prev.customer, [field]: value } }));
  };

  const handleExcursionChange = (field, value) => {
    setFormData(prev => ({ 
      ...prev, 
      excursionDetails: { ...prev.excursionDetails, [field]: value } 
    }));
  };

  const handlePricingChange = (field, value) => {
    setFormData(prev => ({ 
      ...prev, 
      pricing: { ...prev.pricing, [field]: value } 
    }));
  };

  const handlePaymentStatusChange = (field, value) => {
    setFormData(prev => ({ 
      ...prev, 
      paymentStatus: { ...prev.paymentStatus, [field]: value } 
    }));
  };

  const handleOperatorInfoChange = (field, value) => {
    setFormData(prev => ({ 
      ...prev, 
      operatorInfo: { ...prev.operatorInfo, [field]: value } 
    }));
  };

  const addListItem = (listType) => {
    setFormData(prev => ({
      ...prev,
      excursionDetails: {
        ...prev.excursionDetails,
        [listType]: [...(prev.excursionDetails[listType] || []), '']
      }
    }));
  };

  const updateListItem = (listType, index, value) => {
    setFormData(prev => ({
      ...prev,
      excursionDetails: {
        ...prev.excursionDetails,
        [listType]: (prev.excursionDetails[listType] || []).map((item, i) => i === index ? value : item)
      }
    }));
  };

  const removeListItem = (listType, index) => {
    const currentList = formData.excursionDetails[listType] || [];
    if (currentList.length > 1) {
      setFormData(prev => ({
        ...prev,
        excursionDetails: {
          ...prev.excursionDetails,
          [listType]: currentList.filter((_, i) => i !== index)
        }
      }));
    }
  };

  const handleFileUpload = (e) => {
    const files = Array.from(e.target.files);
    files.forEach(file => {
      if (file.size > 5 * 1024 * 1024) {
        alert(`File ${file.name} is too large. Maximum size is 5MB.`);
        return;
      }
      
      const reader = new FileReader();
      reader.onload = (event) => {
        const attachment = {
          id: Date.now() + Math.random(),
          name: file.name,
          type: file.type,
          data: event.target.result,
          size: file.size
        };
        
        setFormData(prev => ({
          ...prev,
          attachments: [...prev.attachments, attachment]
        }));
      };
      reader.readAsDataURL(file);
    });
  };

  const removeAttachment = (id) => {
    setFormData(prev => ({
      ...prev,
      attachments: prev.attachments.filter(att => att.id !== id)
    }));
  };

  const calculateExcursionTotal = () => {
    const { priceType, adultPrice, childPrice, adultCount, childCount, groupPrice } = formData.pricing;
    
    if (priceType === 'per_person') {
      return (adultPrice * adultCount) + (childPrice * childCount);
    } else {
      return groupPrice;
    }
  };

  const calculateTotal = () => {
    const excursionTotal = calculateExcursionTotal();
    return excursionTotal + formData.pricing.conciergeService;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    const excursionTotal = calculateExcursionTotal();
    const total = calculateTotal();
    const { priceType, adultPrice, childPrice, adultCount, childCount } = formData.pricing;

    const items = [
      {
        name: formData.excursionDetails?.title || 'Excursion',
        description: `${formData.excursionDetails?.destination || ''} - ${formData.excursionDetails?.duration || ''}`,
        quantity: priceType === 'per_person' ? ((adultCount || 0) + (childCount || 0)) : 1,
        price: priceType === 'per_person' ? (excursionTotal / ((adultCount || 0) + (childCount || 0)) || 0) : excursionTotal,
        total: excursionTotal
      }
    ];

    if ((formData.pricing?.conciergeService || 0) > 0) {
      items.push({
        name: 'Concierge Service',
        description: 'Travel organization and coordination service',
        quantity: 1,
        price: formData.pricing.conciergeService,
        total: formData.pricing.conciergeService
      });
    }

    const updatedBooking = {
      ...formData,
      id: existingBooking.id,
      number: formData.bookingNumber,
      createdAt: existingBooking.createdAt,
      updatedAt: new Date().toISOString(),
      items,
      subtotal: excursionTotal,
      tax: 0,
      total,
      currency: formData.pricing?.currency || 'EUR',
      companyInfo: settings.travelAgencyInfo,
      colors: settings.colors,
      // Store organization fee as the excursion total for reference
      pricing: {
        ...formData.pricing,
        organizationFee: excursionTotal
      }
    };

    updateInvoice(existingBooking.id, updatedBooking);
    alert('Excursion booking updated successfully!');
    navigate('/invoices');
  };

  const excursionTotal = calculateExcursionTotal();
  const total = calculateTotal();

  return (
    <div className="max-w-6xl mx-auto p-6">
      <div className="flex items-center justify-between mb-6">
        <button onClick={() => navigate('/invoices')} className="flex items-center text-gray-600 hover:text-gray-900">
          <SafeIcon icon={FiArrowLeft} className="w-4 h-4 mr-2" />
          Back to Bookings
        </button>
        <h1 className="text-2xl font-bold text-gray-900">Edit Excursion Booking</h1>
      </div>

      <motion.form
        onSubmit={handleSubmit}
        className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-xl shadow-lg overflow-hidden border-2 border-blue-200"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div className="p-8">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-cyan-600 bg-clip-text text-transparent mb-2">
              Edit Excursion Booking
            </h2>
            <p className="text-gray-600">Update booking confirmation #{formData.bookingNumber}</p>
          </div>

          {/* Booking Number */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
            <div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Booking Reference</h3>
              <div className="flex items-center">
                <div className="relative w-full max-w-xs">
                  <SafeIcon icon={FiHash} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    value={formData.bookingNumber}
                    onChange={(e) => setFormData(prev => ({ ...prev, bookingNumber: e.target.value }))}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Booking Number"
                  />
                </div>
              </div>
            </div>
            <div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Payment Status</h3>
              <div className="bg-white p-4 rounded-lg border border-blue-200">
                <label className="flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.status === 'paid'}
                    onChange={(e) => setFormData(prev => ({ ...prev, status: e.target.checked ? 'paid' : 'unpaid' }))}
                    className="w-5 h-5 text-green-600 border-gray-300 rounded focus:ring-green-500"
                  />
                  <SafeIcon icon={FiCheckSquare} className="w-5 h-5 ml-3 mr-2 text-green-600" />
                  <span className="text-sm font-medium text-gray-700">
                    Mark Booking as Paid
                  </span>
                </label>
              </div>
            </div>
          </div>

          {/* Customer Information */}
          <div className="mb-8">
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Customer Information</h3>
            <CustomerSelector customer={formData.customer} onChange={handleCustomerChange} />
          </div>

          {/* Excursion Details - COMPLETE SECTION */}
          <div className="mb-8">
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Excursion Details</h3>
            <div className="bg-white p-6 rounded-lg border border-blue-200 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Excursion Title *</label>
                  <input
                    type="text"
                    value={formData.excursionDetails.title}
                    onChange={(e) => handleExcursionChange('title', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <SafeIcon icon={FiMapPin} className="inline w-4 h-4 mr-1" />
                    Destination *
                  </label>
                  <input
                    type="text"
                    value={formData.excursionDetails.destination}
                    onChange={(e) => handleExcursionChange('destination', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Tour Operator *</label>
                  <input
                    type="text"
                    value={formData.excursionDetails.tourOperator}
                    onChange={(e) => handleExcursionChange('tourOperator', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Tour Operator Contact</label>
                  <input
                    type="text"
                    value={formData.excursionDetails.tourOperatorContact}
                    onChange={(e) => handleExcursionChange('tourOperatorContact', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <SafeIcon icon={FiCalendar} className="inline w-4 h-4 mr-1" />
                    Excursion Date *
                  </label>
                  <DatePicker
                    selected={formData.excursionDetails.excursionDate}
                    onChange={(date) => handleExcursionChange('excursionDate', date)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    dateFormat="MMMM dd, yyyy"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <SafeIcon icon={FiClock} className="inline w-4 h-4 mr-1" />
                    Duration
                  </label>
                  <input
                    type="text"
                    value={formData.excursionDetails.duration}
                    onChange={(e) => handleExcursionChange('duration', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Meeting Point</label>
                  <input
                    type="text"
                    value={formData.excursionDetails.meetingPoint}
                    onChange={(e) => handleExcursionChange('meetingPoint', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Meeting Time</label>
                  <input
                    type="time"
                    value={formData.excursionDetails.meetingTime}
                    onChange={(e) => handleExcursionChange('meetingTime', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <SafeIcon icon={FiMapPin} className="inline w-4 h-4 mr-1" />
                    Drop-off Location
                  </label>
                  <input
                    type="text"
                    value={formData.excursionDetails.dropOffLocation}
                    onChange={(e) => handleExcursionChange('dropOffLocation', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <SafeIcon icon={FiUsers} className="inline w-4 h-4 mr-1" />
                    Total Participants
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={formData.excursionDetails.participants}
                    onChange={(e) => handleExcursionChange('participants', parseInt(e.target.value) || 1)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Language</label>
                  <select
                    value={formData.excursionDetails.language}
                    onChange={(e) => handleExcursionChange('language', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    {languages.map(lang => (
                      <option key={lang} value={lang}>{lang}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Difficulty Level</label>
                  <select
                    value={formData.excursionDetails.difficulty}
                    onChange={(e) => handleExcursionChange('difficulty', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    {difficulties.map(diff => (
                      <option key={diff} value={diff}>{diff}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* What's Included */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <label className="block text-sm font-medium text-gray-700">What's Included</label>
                  <button
                    type="button"
                    onClick={() => addListItem('includes')}
                    className="text-blue-600 hover:text-blue-800 text-sm flex items-center"
                  >
                    <SafeIcon icon={FiPlus} className="w-4 h-4 mr-1" />
                    Add Item
                  </button>
                </div>
                <div className="space-y-2">
                  {formData.excursionDetails.includes.map((item, index) => (
                    <div key={index} className="flex items-center space-x-2">
                      <input
                        type="text"
                        value={item}
                        onChange={(e) => updateListItem('includes', index, e.target.value)}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                      {formData.excursionDetails.includes.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeListItem('includes', index)}
                          className="text-red-500 hover:text-red-700"
                        >
                          <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* What's Not Included */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <label className="block text-sm font-medium text-gray-700">What's Not Included</label>
                  <button
                    type="button"
                    onClick={() => addListItem('excludes')}
                    className="text-blue-600 hover:text-blue-800 text-sm flex items-center"
                  >
                    <SafeIcon icon={FiPlus} className="w-4 h-4 mr-1" />
                    Add Item
                  </button>
                </div>
                <div className="space-y-2">
                  {formData.excursionDetails.excludes.map((item, index) => (
                    <div key={index} className="flex items-center space-x-2">
                      <input
                        type="text"
                        value={item}
                        onChange={(e) => updateListItem('excludes', index, e.target.value)}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                      {formData.excursionDetails.excludes.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeListItem('excludes', index)}
                          className="text-red-500 hover:text-red-700"
                        >
                          <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Special Requirements */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Special Requirements</label>
                <textarea
                  value={formData.excursionDetails.specialRequirements}
                  onChange={(e) => handleExcursionChange('specialRequirements', e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>
          </div>

          {/* Pricing Details */}
          <div className="mb-8">
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Pricing Details</h3>
            <div className="bg-white p-6 rounded-lg border border-blue-200">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Currency</label>
                  <select
                    value={formData.pricing.currency}
                    onChange={(e) => handlePricingChange('currency', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    {currencies.map(currency => (
                      <option key={currency.code} value={currency.code}>
                        {currency.symbol} {currency.code} - {currency.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Price Type</label>
                  <div className="flex space-x-4">
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="priceType"
                        value="per_person"
                        checked={formData.pricing.priceType === 'per_person'}
                        onChange={(e) => handlePricingChange('priceType', e.target.value)}
                        className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                      />
                      <span className="ml-2 text-sm font-medium text-gray-700">Price per person</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="priceType"
                        value="group"
                        checked={formData.pricing.priceType === 'group'}
                        onChange={(e) => handlePricingChange('priceType', e.target.value)}
                        className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                      />
                      <span className="ml-2 text-sm font-medium text-gray-700">Group price (total)</span>
                    </label>
                  </div>
                </div>
              </div>

              {formData.pricing.priceType === 'per_person' ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Adult Price</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={formData.pricing.adultPrice}
                      onChange={(e) => handlePricingChange('adultPrice', parseFloat(e.target.value) || 0)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Adult Count</label>
                    <input
                      type="number"
                      min="0"
                      value={formData.pricing.adultCount}
                      onChange={(e) => handlePricingChange('adultCount', parseInt(e.target.value) || 0)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Child Price</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={formData.pricing.childPrice}
                      onChange={(e) => handlePricingChange('childPrice', parseFloat(e.target.value) || 0)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Child Count</label>
                    <input
                      type="number"
                      min="0"
                      value={formData.pricing.childCount}
                      onChange={(e) => handlePricingChange('childCount', parseInt(e.target.value) || 0)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>
              ) : (
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Group Price (Total)</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.pricing.groupPrice}
                    onChange={(e) => handlePricingChange('groupPrice', parseFloat(e.target.value) || 0)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              )}

              <div className="border-t border-gray-300 pt-6 mt-6">
                <div className="bg-blue-50 p-4 rounded-lg mb-4">
                  <div className="flex items-start">
                    <SafeIcon icon={FiCheckCircle} className="w-5 h-5 text-blue-600 mr-2 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-gray-900 mb-1">Organization Fee (to pay to operator)</p>
                      <p className="text-sm text-gray-600">
                        This is the amount to be paid to the tour operator: <span className="font-semibold text-blue-700">{getCurrencySymbol(formData.pricing.currency)}{excursionTotal.toFixed(2)}</span>
                      </p>
                    </div>
                  </div>
                  <label className="flex items-center mt-3">
                    <input
                      type="checkbox"
                      checked={formData.paymentStatus.organizationFeePaid}
                      onChange={(e) => handlePaymentStatusChange('organizationFeePaid', e.target.checked)}
                      className="w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
                    />
                    <SafeIcon icon={FiCheckCircle} className="w-4 h-4 ml-2 mr-1 text-green-600" />
                    <span className="text-sm font-medium text-gray-700">Paid to operator</span>
                  </label>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Concierge Service Fee</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.pricing.conciergeService}
                    onChange={(e) => handlePricingChange('conciergeService', parseFloat(e.target.value) || 0)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  {formData.pricing.conciergeService > 0 && (
                    <label className="flex items-center mt-2">
                      <input
                        type="checkbox"
                        checked={formData.paymentStatus.conciergeServicePaid}
                        onChange={(e) => handlePaymentStatusChange('conciergeServicePaid', e.target.checked)}
                        className="w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
                      />
                      <SafeIcon icon={FiCheckCircle} className="w-4 h-4 ml-2 mr-1 text-green-600" />
                      <span className="text-sm font-medium text-gray-700">Paid</span>
                    </label>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Tour Operator Information */}
          <div className="mb-8">
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Tour Operator Information</h3>
            <div className="bg-white p-6 rounded-lg border border-blue-200 space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Confirmation Email Content</label>
                <textarea
                  value={formData.operatorInfo.confirmationEmail}
                  onChange={(e) => handleOperatorInfoChange('confirmationEmail', e.target.value)}
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Contract Details</label>
                <textarea
                  value={formData.operatorInfo.contractDetails}
                  onChange={(e) => handleOperatorInfoChange('contractDetails', e.target.value)}
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Cancellation Policy</label>
                <textarea
                  value={formData.operatorInfo.cancellationPolicy}
                  onChange={(e) => handleOperatorInfoChange('cancellationPolicy', e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Emergency Contact</label>
                <input
                  type="text"
                  value={formData.operatorInfo.emergencyContact}
                  onChange={(e) => handleOperatorInfoChange('emergencyContact', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Additional Notes</label>
                <textarea
                  value={formData.operatorInfo.additionalNotes}
                  onChange={(e) => handleOperatorInfoChange('additionalNotes', e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>
          </div>

          {/* Attachments */}
          <div className="mb-8">
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Attachments</h3>
            <div className="bg-white p-6 rounded-lg border border-blue-200">
              <div className="mb-4">
                <label className="flex items-center px-4 py-2 bg-blue-50 border border-blue-300 rounded-lg cursor-pointer hover:bg-blue-100 transition-colors">
                  <SafeIcon icon={FiUpload} className="w-4 h-4 mr-2 text-blue-600" />
                  <span className="text-sm text-blue-700">Upload Files (PDF, Images, Documents)</span>
                  <input
                    type="file"
                    multiple
                    accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.txt"
                    onChange={handleFileUpload}
                    className="hidden"
                  />
                </label>
                <p className="text-xs text-gray-500 mt-1">Maximum file size: 5MB per file</p>
              </div>

              {formData.attachments.length > 0 && (
                <div className="space-y-2">
                  <h4 className="text-sm font-medium text-gray-700">Uploaded Files:</h4>
                  {formData.attachments.map((attachment) => (
                    <div key={attachment.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center">
                        <SafeIcon icon={FiFileText} className="w-4 h-4 text-gray-500 mr-2" />
                        <span className="text-sm text-gray-700">{attachment.name}</span>
                        <span className="text-xs text-gray-500 ml-2">
                          ({(attachment.size / 1024 / 1024).toFixed(2)} MB)
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeAttachment(attachment.id)}
                        className="text-red-500 hover:text-red-700"
                      >
                        <SafeIcon icon={FiX} className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Summary */}
          <div className="bg-gradient-to-br from-blue-100 to-cyan-100 p-6 rounded-lg mb-8 border border-blue-300">
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Booking Summary</h3>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-gray-700">Excursion Total:</span>
                <span className="font-medium">{getCurrencySymbol(formData.pricing.currency)}{excursionTotal.toFixed(2)}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-700">Organization Fee (to operator):</span>
                <div className="flex items-center">
                  <span className="font-medium mr-2">{getCurrencySymbol(formData.pricing.currency)}{excursionTotal.toFixed(2)}</span>
                  {formData.paymentStatus.organizationFeePaid && (
                    <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full">Paid</span>
                  )}
                </div>
              </div>
              {formData.pricing.conciergeService > 0 && (
                <div className="flex justify-between">
                  <span className="text-gray-700">Concierge Service:</span>
                  <div className="flex items-center">
                    <span className="font-medium mr-2">{getCurrencySymbol(formData.pricing.currency)}{formData.pricing.conciergeService.toFixed(2)}</span>
                    {formData.paymentStatus.conciergeServicePaid && (
                      <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full">Paid</span>
                    )}
                  </div>
                </div>
              )}
              <div className="border-t border-blue-300 pt-2 mt-2">
                <div className="flex justify-between text-lg font-bold">
                  <span>Total:</span>
                  <span className="text-blue-600">{getCurrencySymbol(formData.pricing.currency)}{total.toFixed(2)}</span>
                </div>
              </div>
            </div>
          </div>

          <motion.button
            type="submit"
            className="w-full bg-gradient-to-r from-green-600 to-teal-600 text-white py-4 px-6 rounded-lg font-semibold text-lg hover:from-green-700 hover:to-teal-700 transition-all duration-300 flex items-center justify-center shadow-lg"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <SafeIcon icon={FiSave} className="w-5 h-5 mr-2" />
            Update Booking Confirmation
          </motion.button>
        </div>
      </motion.form>
    </div>
  );
};

export default ExcursionBookingEdit;